import * as React from 'react';
import { AppContext } from '../../../App';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import useGlynt from '../../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../../errorResponses';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';

export interface TrainingRevisionDeleteProps {
  trainingRevisionId: string;
  trainingRevisionName: string;
}

// TODO: possible api issue that the training revision is not delete afte the request is success submitted
// example in stage /data-pools/OD37w1/train/hIjeG1
const TrainingRevisionDelete: React.FC<TrainingRevisionDeleteProps> = ({
  trainingRevisionId,
  trainingRevisionName,
}) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const theme = useTheme();

  const deleteTrainingRevisionMutation = useMutation({
    mutationFn: async () => (await glynt).trainingRevision.remove(trainingRevisionId),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingRevisions']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Training revision deleted successfully.',
      });
    },

    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeleteTrainingRevision,
      });
    }
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={`Delete Training Revision`}
        disableFocusListener
        disableTouchListener
      >
        <DeleteIcon
          data-cy={`btn-delete-training-revision-${trainingRevisionName}`}
          onClick={!deleteTrainingRevisionMutation.isPending ? handleClickOpen : undefined}
          style={{
            color: deleteTrainingRevisionMutation.isPending
              ? '#ccc'
              : theme.palette.custom.lightGreen,
            cursor: deleteTrainingRevisionMutation.isPending ? 'not-allowed' : 'pointer',
            fontSize: '21px',
          }}
        />
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this training revision?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => deleteTrainingRevisionMutation.mutate()}
            autoFocus
            data-cy="btn-training-revision-delete-modal-yes"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrainingRevisionDelete;
